import { Star, Info, Navigation, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import RoleSwitcher from "@/components/RoleSwitcher";
import { AccountMenu } from "@/components/AccountMenu";

/**
 * Sticky header for courier dashboard.
 * Shows online status, active orders badge, about info, rating, profile, logout.
 */
export const CourierHeader = ({
  user,
  onUserUpdate,
  isAvailable,
  activeCount,
  rating,
  userInitials,
  onScrollToOrders,
  onOpenAbout,
  onOpenProfile,
  onLogout,
}) => {
  return (
    <div
      className={`p-6 transition-all duration-500 ${
        isAvailable ? "bg-emerald-950" : "bg-slate-900"
      } sticky top-0 z-50 border-b border-slate-800`}
      style={{ transform: "translateZ(0)" }}
    >
      <div className="flex justify-between items-center max-w-5xl mx-auto">
        <div className="flex items-center gap-3">
          <img
            src="/woma-logo.png"
            alt="WÔMA de ARTEKNOS"
            className="w-9 h-9 rounded-full object-cover ring-2 ring-[#D4AF37]/50 shadow-[0_0_10px_rgba(212,175,55,0.3)] flex-shrink-0"
            data-testid="courier-header-logo"
          />
          <div
            className={`w-3 h-3 rounded-full ${
              isAvailable ? "bg-emerald-500 animate-pulse" : "bg-slate-500"
            }`}
          />
          <span
            className="font-black italic tracking-widest text-xs md:text-sm uppercase"
            data-testid="courier-status-label"
          >
            {isAvailable ? "En Ligne" : "Hors Ligne"}
          </span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0">
          <RoleSwitcher user={user} onUserUpdate={onUserUpdate} className="hidden sm:block" />
          {activeCount > 0 && (
            <button
              onClick={onScrollToOrders}
              className="relative w-10 h-10 rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/40 hover:bg-[#D4AF37]/25 flex items-center justify-center transition-colors"
              data-testid="header-active-orders-badge"
              title={`${activeCount} livraison${activeCount > 1 ? "s" : ""} en cours`}
              aria-label="Mes livraisons"
            >
              <Navigation size={16} className="text-[#D4AF37]" />
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center border-2 border-[#0F172A] animate-pulse">
                {activeCount}
              </span>
            </button>
          )}
          <button
            onClick={onOpenAbout}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] font-black uppercase tracking-widest text-slate-300 hover:text-white transition-colors"
            data-testid="courier-about-btn"
            title="Qui sommes-nous ?"
          >
            <Info size={12} /> Qui sommes-nous ?
          </button>
          <button
            onClick={onOpenAbout}
            className="sm:hidden w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white flex items-center justify-center"
            data-testid="courier-about-btn-mobile"
            title="Qui sommes-nous ?"
            aria-label="Qui sommes-nous ?"
          >
            <Info size={14} />
          </button>
          <div className="text-right hidden sm:block">
            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Note</p>
            <div className="flex items-center gap-1 text-amber-400 font-black text-xs">
              <Star size={12} fill="currentColor" /> {(rating || 5).toFixed(2)}
            </div>
          </div>
          <button
            onClick={onOpenProfile}
            className="w-11 h-11 rounded-2xl border-2 border-[#D4AF37] p-0.5 hover:opacity-90"
            title="Mon profil"
            data-testid="profile-btn"
          >
            <div className="w-full h-full rounded-[0.7rem] bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center text-[#0F172A] font-black text-sm">
              {userInitials}
            </div>
          </button>
          <Button
            onClick={onLogout}
            size="sm"
            className="hidden sm:inline-flex rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-red-400 border border-white/10 px-3"
            data-testid="courier-logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </Button>
          <AccountMenu user={user} onUserUpdate={onUserUpdate} onLogout={onLogout} className="sm:hidden" />
        </div>
      </div>
    </div>
  );
};
